import { createContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { EnvelopeSchema, type AcesEnvelope, type AcesEventInput } from './types';

type Sink = (env: AcesEnvelope) => void;

export const EventBusContext = createContext<((evt: AcesEventInput) => void) | null>(null);

const DEV_LOG = import.meta.env.DEV;
const LOCAL_BUFFER_KEY = 'aces.event.buffer.v1';
const BUFFER_MAX = 500;

/**
 * Initial sink stack:
 *  1. Console logger in dev (so we can watch the funnel as we build).
 *  2. Local ring buffer in sessionStorage (so a future tutor agent can read
 *     recent interactions without a backend).
 *  3. Future: POST to /api/telemetry — wired through env flag.
 */
function buildSinks(): Sink[] {
  const sinks: Sink[] = [];
  if (DEV_LOG) sinks.push((e) => console.debug('[aces:event]', e.kind, e));
  sinks.push((e) => {
    try {
      const raw = sessionStorage.getItem(LOCAL_BUFFER_KEY);
      const buf: AcesEnvelope[] = raw ? JSON.parse(raw) : [];
      buf.push(e);
      if (buf.length > BUFFER_MAX) buf.splice(0, buf.length - BUFFER_MAX);
      sessionStorage.setItem(LOCAL_BUFFER_KEY, JSON.stringify(buf));
    } catch {
      /* swallow — telemetry must never break UX */
    }
  });
  return sinks;
}

export function EventBusProvider({ sessionId, children }: { sessionId: string; children: ReactNode }) {
  const sinksRef = useRef<Sink[]>([]);
  if (sinksRef.current.length === 0) sinksRef.current = buildSinks();

  const emit = useCallback((evt: AcesEventInput) => {
    const envelope: AcesEnvelope = {
      sessionId,
      ts: Date.now(),
      path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      ...evt,
    };
    const parsed = EnvelopeSchema.safeParse(envelope);
    if (!parsed.success) {
      if (DEV_LOG) console.warn('[aces:event] dropped malformed event', parsed.error.flatten(), envelope);
      return;
    }
    for (const sink of sinksRef.current) sink(parsed.data);
  }, [sessionId]);

  useEffect(() => {
    // Expose a read-only window helper for the future tutor's in-page agent.
    (window as unknown as { __ACES_BUS__?: { recent: () => AcesEnvelope[] } }).__ACES_BUS__ = {
      recent: () => {
        try { return JSON.parse(sessionStorage.getItem(LOCAL_BUFFER_KEY) ?? '[]'); }
        catch { return []; }
      },
    };
  }, []);

  const ctxValue = useMemo(() => emit, [emit]);
  return <EventBusContext.Provider value={ctxValue}>{children}</EventBusContext.Provider>;
}
